import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { FormattedDateTime } from '@10xLabs/react-web/components/FormattedDateTime'
import { FormattedPercent } from '@10xLabs/react-web/components/FormattedPercent'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { CampaignStatus } from 'features/Campaigns/components/Status'
import { DISCOUNT_TYPE } from 'features/Coupons/components/DiscountType/constants'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { getStatus } from 'resolvers/campaigns'
import { isSuperAdmin, canWrite } from 'features/Users/utils'
import { SnacksCouponsActions } from './Actions'
import { useLogin } from 'features/Login/hooks'
import { GTCoupon } from 'gql/types.generated'

const DurationWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  > div {
    margin-left: 2px;
  }
`

const MinSaleWrapper = styled.div`
  white-space: nowrap;
`

export const SnacksCouponsList = ({ coupons = [] }: { coupons: GTCoupon[] }): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canWrite(FEATURES.CAMPAIGN, login.accessToken.policies) && <Th />}
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.code" defaultMessage="Code" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.campaign" defaultMessage="Campaign" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.discount" defaultMessage="Discount" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.duration" defaultMessage="Duration" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.usage" defaultMessage="Usage" />
            </Th>
            {isSuperAdmin(login.accessToken.conditions) && (
              <Th>
                <FormattedMessage id="dictionary.company" defaultMessage="Company" />
              </Th>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {coupons.map((coupon): ReactElement => {
            return (
              <Tr key={coupon.id}>
                {canWrite(FEATURES.CAMPAIGN, login.accessToken.policies) && (
                  <Td>
                    <SnacksCouponsActions coupon={coupon} />
                  </Td>
                )}
                <Td>
                  <CampaignStatus status={getStatus(coupon)} />
                </Td>
                <Td>{coupon.code}</Td>
                <Td>{coupon.campaign.name}</Td>
                <Td>
                  <div>
                    {coupon.discountType === DISCOUNT_TYPE.PERCENTAGE ? (
                      <FormattedPercent value={coupon.value} />
                    ) : (
                      <FormattedPrice value={coupon.value} />
                    )}
                  </div>
                  {(coupon.constraints.minSale || 0) > 0 && (
                    <MinSaleWrapper>
                      <FormattedMessage id="coupons.snacks.index.miinSale.label" defaultMessage="min." />{' '}
                      <FormattedPrice value={coupon.constraints.minSale} />
                    </MinSaleWrapper>
                  )}
                </Td>
                <Td>
                  <DurationWrapper>
                    <FormattedMessage id="coupons.snacks.index.startsAt.label" defaultMessage="Starts:" />

                    <FormattedDateTime date={coupon.startsAt} intl={intl} />
                  </DurationWrapper>
                  <DurationWrapper>
                    <FormattedMessage id="coupons.snacks.index.endsAt.label" defaultMessage="Ends:" />
                    <FormattedDateTime date={coupon.endsAt} intl={intl} />
                  </DurationWrapper>
                </Td>
                <Td>
                  <div>
                    <FormattedMessage
                      id="coupons.snacks.index.usage.value"
                      defaultMessage="{usedCount} of {limit}"
                      values={{
                        usedCount: coupon.usedCount,
                        limit: coupon.constraints.limit
                      }}
                    />
                  </div>
                </Td>
                {isSuperAdmin(login.accessToken.conditions) && <Td>{coupon.campaign.company.name}</Td>}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
